/**
 * TerrainDeformConfig — parameters for snow / sand / mud render-to-heightmap.
 *
 * The canonical reference is the GDC 2014 Batman: Arkham Origins talk
 * (Cédric Pinson, WB Montréal): a top-down ortho-projected heightmap with
 * per-frame additive deformation, persistent across frames within an
 * active window centered on the player.
 *
 * v1 ships:
 *   - Single-channel R-32F CPU buffer + RGBA16F GPU texture
 *   - Static world-space bounds (caller decides — follow-camera scroll is v2)
 *   - Sphere + capsule deformer footprints with Gaussian falloff
 *   - Persistence by default (no decay) — opt-in slow refill
 *   - Two-layer support: deformation depth feeds the layer mask formula
 *     `t = saturate((depth - L) / (U - L))` for snow-vs-powder blending
 *
 * v2 ships:
 *   - Camera-follow toroidal-wrap heightmap (follows player at texel grid)
 *   - Persistent disk-tile macro layer (Decima-style trail-blazing system)
 *   - GPU compute deformer rasterization (currently CPU)
 *   - Hardware-tessellation / compute-displaced terrain (currently vertex)
 *   - Reoriented Normal Mapping (RNM) for layer normal blending
 */

export interface TerrainDeformQualityPreset {
  /** Heightmap resolution (square texture). GDC 2014 explicitly calls out
   * that LOWER resolution looks better than too-high — per-texel footprints
   * are easier to read than pixel-sized noise. */
  resolution: number;
  /** Max simultaneous deformer agents per frame (CPU budget gate). */
  maxDeformers: number;
}

export const TERRAIN_DEFORM_PRESETS: Record<'low' | 'medium' | 'high' | 'ultra', TerrainDeformQualityPreset> = {
  low:    { resolution: 128, maxDeformers:  16 },
  medium: { resolution: 256, maxDeformers:  32 },
  high:   { resolution: 512, maxDeformers:  64 },
  ultra:  { resolution: 1024, maxDeformers: 128 },
};

export interface TerrainDeformConfig {
  /** Quality preset (sets resolution + agent cap). */
  quality: TerrainDeformQualityPreset;
  /**
   * World-space XZ bounds the heightmap covers. v1 treats this as static —
   * v2 will support follow-camera scrolling.
   */
  bounds: {
    minX: number; minZ: number;
    maxX: number; maxZ: number;
  };
  /**
   * Maximum depression depth (world units, e.g. meters of snow). Capped to
   * prevent a stationary heavy agent from "drilling" through the surface.
   */
  maxDepth: number;
  /**
   * Refill rate per second. 0 = permanent footprints (snow stays compressed
   * forever — Arkham Origins / Spider-Man / Horizon ship this). Positive =
   * slow recovery (mud slowly fills back in).
   */
  refillRate: number;
  /**
   * If true, the heightmap auto-uploads each frame via the system. Set
   * false for manual control (skip uploads when no deformers are active).
   */
  autoUpload: boolean;
  /**
   * Two-layer config: the depth at which the powder layer transitions to
   * the compacted layer below. Used as a uniform for snow-style shaders.
   */
  layerSplitDepth: number;
}

export function createTerrainDeformConfig(overrides: Partial<TerrainDeformConfig> = {}): TerrainDeformConfig {
  const quality = overrides.quality ?? TERRAIN_DEFORM_PRESETS.medium;
  return {
    quality,
    bounds: overrides.bounds ?? { minX: -32, minZ: -32, maxX: 32, maxZ: 32 },
    maxDepth: overrides.maxDepth ?? 0.15,
    refillRate: overrides.refillRate ?? 0,
    autoUpload: overrides.autoUpload ?? true,
    layerSplitDepth: overrides.layerSplitDepth ?? 0.04,
  };
}

/**
 * Deformer agent footprint shapes.
 *
 * - Sphere: radial Gaussian — a single foot, paw, or impact point.
 * - Capsule: stretched between two endpoints — a vehicle wheel or skid mark.
 *
 * `depth` is the depression depth applied at the center; the shape falls
 * off with a Gaussian to zero at the boundary radius.
 */
export type TerrainDeformer =
  | {
      kind: 'sphere';
      x: number; z: number;
      radius: number;
      depth: number;
    }
  | {
      kind: 'capsule';
      x0: number; z0: number;
      x1: number; z1: number;
      radius: number;
      depth: number;
    };
